import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth.service';
import { userService } from '../services/user.service';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Déconnexion
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.clear();
    }
  }, []);

  // Initialiser l'utilisateur au chargement
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const storedUser = authService.getCurrentUser();

        if (token && storedUser) {
          // Vérifier que le token est toujours valide en récupérant le profil
          try {
            const profile = await userService.getMyProfile();
            setUser(profile);
            setIsAuthenticated(true);
          } catch (error) {
            // Token invalide, nettoyer
            console.error('Token invalide:', error);
            logout();
          }
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de l\'authentification:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [logout]);

  // Connexion
  const login = useCallback(async (email, password) => {
    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  // Inscription
  const register = useCallback(async (userData) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  // Rafraîchir le profil utilisateur
  const refreshProfile = useCallback(async () => {
    try {
      const profile = await userService.getMyProfile();
      setUser(profile);
      return profile;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du profil:', error);
      throw error;
    }
  }, []);

  // Mettre à jour le profil
  const updateProfile = useCallback(async (userData) => {
    try {
      const updatedUser = await userService.updateMyProfile(userData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  }, []);

  // Vérifier les permissions
  const hasRole = useCallback(
    (role) => {
      return user?.roles?.includes(role) || false;
    },
    [user]
  );

  const isAdmin = useCallback(() => {
    return hasRole('ROLE_ADMIN');
  }, [hasRole]);

  // Note: Le rôle SUPER_ADMIN n'existe plus - ADMIN a maintenant tous les droits
  // Cette fonction est conservée pour la rétrocompatibilité mais retourne toujours false
  const isSuperAdmin = useCallback(() => {
    return false;
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshProfile,
    updateProfile,
    hasRole,
    isAdmin,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
